import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface AppSelectOption {
  value: any;
  label: string;
  disabled?: boolean;
  icon?: string;
  sublabel?: string;
}

@Component({
  selector: 'app-select',
  templateUrl: './app-select.component.html',
  styleUrls: ['./app-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppSelectComponent),
      multi: true,
    },
  ],
})
export class AppSelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() customClass: string = '';
  @Input() containerClass: string = '';
  @Input() height: string | number = '';
  @Input() labelText: string = '';
  @Input() label: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;
  @Input() optional: boolean = false;
  @Input() tooltip: string = '';
  @Input() labelIcon: string = '';
  @Input() labelSize: 'sm' | 'md' | 'lg' = 'md';

  get resolvedLabel(): string {
    return this.labelText || this.label || '';
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  @Input() options: any[] = [];
  @Input() placeholder: string = 'Select...';
  @Input() disabled: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() helperText: string = '';
  @Input() searchable: boolean = false;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() clearable: boolean = false;
  @Input() prefixIcon: string = '';

  @Input() size: 'sm' | 'md' | 'lg' | 'compact' = 'md';
  @Input() footerBtnName: string = '';
  @Input() footerBtnIcon: boolean = true;
  @Input() emptyText: string = 'No options found';
  @Input() dropdownMinWidth: string = '';
  @Input() resetOnSelect: boolean = false;
  @Input() placement: 'auto' | 'top' | 'bottom' = 'auto';

  @Output() selectionChange = new EventEmitter<any>();
  @Output() optionClick = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() clearClick = new EventEmitter<void>();
  @Output() footerBtnClick = new EventEmitter<MouseEvent>();

  @Input() value: any = null;
  isOpen: boolean = false;
  actualPlacement: 'top' | 'bottom' = 'bottom';
  searchTerm: string = '';
  formattedOptions: AppSelectOption[] = [];
  filteredOptions: AppSelectOption[] = [];
  selectedOption: AppSelectOption | undefined = undefined;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.formatOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['value']) {
      if (changes['value']) {
        this.value = changes['value'].currentValue !== undefined ? changes['value'].currentValue : null;
      }
      this.formatOptions();
      this.cdr.markForCheck();
    }
  }

  private isOptionEqual(optVal: any, targetVal: any): boolean {
    if (optVal === targetVal) return true;
    if (optVal != null && targetVal != null && typeof optVal !== 'object' && typeof targetVal !== 'object' && optVal == targetVal) {
      return true;
    }
    if (optVal && targetVal) {
      const norm = (s: any) => String(s ?? '').trim().toLowerCase();

      // If one is object and the other is string / primitive
      if (typeof optVal === 'object' && typeof targetVal !== 'object') {
        const tgt = norm(targetVal);
        if (!tgt) return false;
        if (optVal.isoCode && norm(optVal.isoCode) === tgt) return true;
        if (optVal.code && norm(optVal.code) === tgt) return true;
        if (optVal.name && norm(optVal.name) === tgt) return true;
        if (optVal.value !== undefined && norm(optVal.value) === tgt) return true;
        if (optVal.id !== undefined && norm(optVal.id) === tgt) return true;
      }
      if (typeof targetVal === 'object' && typeof optVal !== 'object') {
        const opt = norm(optVal);
        if (!opt) return false;
        if (targetVal.isoCode && norm(targetVal.isoCode) === opt) return true;
        if (targetVal.code && norm(targetVal.code) === opt) return true;
        if (targetVal.name && norm(targetVal.name) === opt) return true;
        if (targetVal.value !== undefined && norm(targetVal.value) === opt) return true;
        if (targetVal.id !== undefined && norm(targetVal.id) === opt) return true;
      }
      // If both are objects
      if (typeof optVal === 'object' && typeof targetVal === 'object') {
        if (optVal.isoCode && targetVal.isoCode) {
          return norm(optVal.isoCode) === norm(targetVal.isoCode);
        }
        if (optVal.code && targetVal.code) {
          return norm(optVal.code) === norm(targetVal.code);
        }
        if (optVal.id !== undefined && targetVal.id !== undefined) {
          return norm(optVal.id) === norm(targetVal.id);
        }
        if (optVal.name && targetVal.name) {
          return norm(optVal.name) === norm(targetVal.name);
        }
        if (optVal.value !== undefined && targetVal.value !== undefined) {
          return norm(optVal.value) === norm(targetVal.value);
        }
      }
    }
    return false;
  }

  isOptionSelected(opt: AppSelectOption): boolean {
    return this.isOptionEqual(opt.value, this.value);
  }

  private updateSelectedOption(): void {
    if (this.value === null || this.value === undefined || this.value === '') {
      this.selectedOption = undefined;
      return;
    }
    this.selectedOption = this.formattedOptions.find((o) => this.isOptionEqual(o.value, this.value));
  }

  private updateFilteredOptions(): void {
    if (!this.searchTerm || !this.searchable) {
      this.filteredOptions = this.formattedOptions;
      return;
    }
    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.formattedOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(term) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(term))
    );
  }

  private formatOptions(): void {
    if (!Array.isArray(this.options)) {
      this.formattedOptions = [];
      this.filteredOptions = [];
      this.updateSelectedOption();
      return;
    }

    this.formattedOptions = this.options.map((opt) => {
      if (opt !== null && typeof opt === 'object' && 'value' in opt && 'label' in opt) {
        return opt as AppSelectOption;
      }
      return {
        value: opt,
        label: String(opt),
      };
    });
    this.updateFilteredOptions();
    this.updateSelectedOption();
  }

  writeValue(val: any): void {
    this.value = val !== undefined ? val : null;
    this.updateSelectedOption();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isOpen = false;
    }
    this.cdr.markForCheck();
  }

  private calculatePlacement(): void {
    if (this.placement === 'top') {
      this.actualPlacement = 'top';
      return;
    }
    if (this.placement === 'bottom') {
      this.actualPlacement = 'bottom';
      return;
    }

    try {
      const triggerEl = this.elementRef.nativeElement.querySelector('.app-select-trigger');
      if (triggerEl) {
        const rect = triggerEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 250;

        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          this.actualPlacement = 'top';
        } else {
          this.actualPlacement = 'bottom';
        }
      }
    } catch {
      this.actualPlacement = 'bottom';
    }
  }

  toggleDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.calculatePlacement();
      this.searchTerm = '';
      this.updateFilteredOptions();
    } else {
      this.onTouched();
    }
  }

  selectOption(opt: AppSelectOption, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (opt.disabled) return;

    if (this.resetOnSelect) {
      this.value = null;
      this.selectedOption = undefined;
      this.onChange(opt.value);
      this.selectionChange.emit(opt.value);
      this.optionClick.emit(opt.value);
    } else {
      this.value = opt.value;
      this.updateSelectedOption();
      this.onChange(opt.value);
      this.selectionChange.emit(opt.value);
      this.optionClick.emit(opt.value);
    }
    this.isOpen = false;
    this.onTouched();
  }

  handleClear(event: MouseEvent): void {
    event.stopPropagation();
    this.value = null;
    this.selectedOption = undefined;
    this.onChange(null);
    this.selectionChange.emit(null);
    this.clearClick.emit();
    this.onTouched();
  }

  handleClearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.searchTerm = '';
    this.updateFilteredOptions();
    this.searchChange.emit('');
  }

  onFooterBtnClick(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen = false;
    this.onTouched();
    this.footerBtnClick.emit(event);
  }

  onSearchInput(event: any): void {
    this.searchTerm = event.target.value || '';
    this.updateFilteredOptions();
    this.searchChange.emit(this.searchTerm);
  }

  get selectedLabel(): string {
    return this.selectedOption ? this.selectedOption.label : '';
  }

  trackByOption(index: number, opt: AppSelectOption): any {
    return opt.label || opt.value || index;
  }

  get hasError(): boolean {
    return !!this.errorMsg && this.errorMsg.trim().length > 0;
  }

  get resolvedHeight(): string | null {
    if (this.height) {
      return typeof this.height === 'number' ? `${this.height}px` : this.height;
    }
    return null;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isOpen) {
        this.isOpen = false;
        this.onTouched();
      }
    }
  }
}
